/**
 * Astitva Living — Theme Global JavaScript
 * Handles: dark mode toggle, back-to-top, header scroll behaviour
 * No dependencies. Vanilla JS only.
 */

(function () {
  'use strict';

  /* =============================================
     DARK MODE TOGGLE
  ============================================= */
  const ThemeToggle = {
    btn: null,
    sunIcon: null,
    moonIcon: null,
    STORAGE_KEY: 'astitva-theme',
    DARK_CLASS: 'theme-dark',

    init() {
      this.btn = document.getElementById('theme-toggle');
      if (!this.btn) return;

      this.sunIcon  = this.btn.querySelector('.theme-toggle__sun');
      this.moonIcon = this.btn.querySelector('.theme-toggle__moon');

      this.updateIcons();

      this.btn.addEventListener('click', () => {
        const isDark = document.documentElement.classList.toggle(this.DARK_CLASS);
        try {
          localStorage.setItem(this.STORAGE_KEY, isDark ? 'dark' : 'light');
        } catch (e) {}
        this.updateIcons();
      });
    },

    updateIcons() {
      const isDark = document.documentElement.classList.contains(this.DARK_CLASS);
      if (this.sunIcon)  this.sunIcon.hidden  = !isDark;
      if (this.moonIcon) this.moonIcon.hidden = isDark;
    }
  };

  /* =============================================
     BACK TO TOP
  ============================================= */
  const BackToTop = {
    btn: null,
    VISIBLE_CLASS: 'is-visible',
    SCROLL_THRESHOLD: 400,

    init() {
      this.btn = document.getElementById('back-to-top');
      if (!this.btn) return;

      window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });

      this.btn.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    },

    handleScroll() {
      if (window.scrollY > this.SCROLL_THRESHOLD) {
        this.btn.classList.add(this.VISIBLE_CLASS);
      } else {
        this.btn.classList.remove(this.VISIBLE_CLASS);
      }
    }
  };

  /* =============================================
     STICKY HEADER (transparent → solid on scroll)
  ============================================= */
  const StickyHeader = {
    header: null,
    SCROLLED_CLASS: 'header--scrolled',
    THRESHOLD: 60,

    init() {
      this.header = document.getElementById('site-header');
      if (!this.header) return;

      window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
      this.handleScroll();
    },

    handleScroll() {
      if (window.scrollY > this.THRESHOLD) {
        this.header.classList.add(this.SCROLLED_CLASS);
      } else {
        this.header.classList.remove(this.SCROLLED_CLASS);
      }
    }
  };

  /* =============================================
     MOBILE NAV TOGGLE
  ============================================= */
  const MobileNav = {
    toggle: null,
    nav: null,
    OPEN_CLASS: 'nav-open',

    init() {
      this.toggle = document.getElementById('mobile-nav-toggle');
      this.nav    = document.getElementById('mobile-nav');
      if (!this.toggle || !this.nav) return;

      this.toggle.addEventListener('click', () => {
        const isOpen = this.nav.hasAttribute('open');
        if (isOpen) {
          this.close();
        } else {
          this.open();
        }
      });

      // Close on backdrop click or ESC
      this.nav.addEventListener('click', (e) => {
        if (e.target === this.nav) this.close();
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    },

    open() {
      this.nav.setAttribute('open', '');
      document.documentElement.setAttribute('scroll-lock', '');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.toggle.setAttribute('aria-label', 'Close menu');
    },

    close() {
      this.nav.removeAttribute('open');
      document.documentElement.removeAttribute('scroll-lock');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.toggle.setAttribute('aria-label', 'Open menu');
    }
  };

  /* =============================================
     ANNOUNCEMENT BAR DISMISS
  ============================================= */
  const AnnouncementBar = {
    bar: null,
    closeBtn: null,
    STORAGE_KEY: 'astitva-announcement-dismissed',

    init() {
      this.bar = document.getElementById('announcement-bar');
      if (!this.bar) return;

      // Check if already dismissed in this session
      try {
        if (sessionStorage.getItem(this.STORAGE_KEY)) {
          this.bar.hidden = true;
          return;
        }
      } catch (e) {}

      this.closeBtn = this.bar.querySelector('[data-announcement-close]');
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => {
          this.bar.hidden = true;
          try { sessionStorage.setItem(this.STORAGE_KEY, '1'); } catch (e) {}
        });
      }
    }
  };

  /* =============================================
     NAV DROPDOWN ACCESSIBILITY
     Keyboard navigation for desktop dropdowns
  ============================================= */
  const NavDropdowns = {
    init() {
      const navItems = document.querySelectorAll('.nav__item--has-dropdown');

      navItems.forEach(item => {
        const trigger = item.querySelector('.nav__link');
        const dropdown = item.querySelector('.nav__dropdown');
        if (!trigger || !dropdown) return;

        // Open on mouseenter
        item.addEventListener('mouseenter', () => {
          dropdown.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        });

        // Close on mouseleave
        item.addEventListener('mouseleave', () => {
          dropdown.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
        });

        // Keyboard: Enter/Space toggles
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const isOpen = dropdown.classList.toggle('is-open');
            trigger.setAttribute('aria-expanded', String(isOpen));
          }
          if (e.key === 'Escape') {
            dropdown.classList.remove('is-open');
            trigger.setAttribute('aria-expanded', 'false');
            trigger.focus();
          }
        });
      });

      // Close all dropdowns on outside click
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.nav__item--has-dropdown')) {
          document.querySelectorAll('.nav__dropdown.is-open').forEach(d => {
            d.classList.remove('is-open');
          });
        }
      });
    }
  };

  /* =============================================
     CART QUANTITY UPDATER
     For the /cart page quantity inputs
  ============================================= */
  const CartQuantity = {
    init() {
      document.querySelectorAll('[data-qty-input]').forEach(wrapper => {
        const minusBtn = wrapper.querySelector('[data-qty-minus]');
        const plusBtn  = wrapper.querySelector('[data-qty-plus]');
        const input    = wrapper.querySelector('[data-qty-value]');
        if (!minusBtn || !plusBtn || !input) return;

        minusBtn.addEventListener('click', () => {
          const current = parseInt(input.value, 10);
          if (current > 1) {
            input.value = current - 1;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });

        plusBtn.addEventListener('click', () => {
          const current = parseInt(input.value, 10);
          input.value = current + 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  };

  /* =============================================
     ACCORDION (FAQ, product tabs)
  ============================================= */
  const Accordion = {
    init() {
      document.querySelectorAll('[data-accordion]').forEach(accordion => {
        const items = accordion.querySelectorAll('[data-accordion-item]');

        items.forEach(item => {
          const trigger = item.querySelector('[data-accordion-trigger]');
          const content = item.querySelector('[data-accordion-content]');
          if (!trigger || !content) return;

          trigger.addEventListener('click', () => {
            const isOpen = item.hasAttribute('data-open');

            // Close all siblings
            items.forEach(sibling => {
              sibling.removeAttribute('data-open');
              sibling.querySelector('[data-accordion-trigger]')
                ?.setAttribute('aria-expanded', 'false');
            });

            // Toggle current
            if (!isOpen) {
              item.setAttribute('data-open', '');
              trigger.setAttribute('aria-expanded', 'true');
            }
          });
        });
      });
    }
  };

  /* =============================================
     IMAGE SNIPPET ENHANCEMENTS
     Adds natural lazy loading fallback
  ============================================= */
  const Images = {
    init() {
      // Add native lazy loading to all non-critical images
      document.querySelectorAll('img:not([loading])').forEach(img => {
        img.setAttribute('loading', 'lazy');
      });
    }
  };

  /* =============================================
     PREDICTIVE SEARCH
  ============================================= */
  const SearchOverlay = {
    overlay: null,
    input: null,
    results: null,
    debounceTimer: null,
    DEBOUNCE_MS: 300,

    init() {
      this.overlay = document.getElementById('search-overlay');
      if (!this.overlay) return;

      this.input   = this.overlay.querySelector('[data-search-input]');
      this.results = this.overlay.querySelector('[data-search-results]');

      const openBtns  = document.querySelectorAll('[data-search-open]');
      const closeBtn  = this.overlay.querySelector('[data-search-close]');

      openBtns.forEach(btn => {
        btn.addEventListener('click', () => this.open());
      });

      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.close());
      }

      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) this.close();
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.overlay.hidden) this.close();
      });

      if (this.input) {
        this.input.addEventListener('input', () => this.handleInput());
      }
    },

    open() {
      this.overlay.hidden = false;
      this.overlay.setAttribute('open', '');
      document.documentElement.setAttribute('scroll-lock', '');
      if (this.input) this.input.focus();
    },

    close() {
      this.overlay.hidden = true;
      this.overlay.removeAttribute('open');
      document.documentElement.removeAttribute('scroll-lock');
      if (this.results) this.results.innerHTML = '';
    },

    handleInput() {
      clearTimeout(this.debounceTimer);
      const query = this.input.value.trim();

      if (query.length < 2) {
        if (this.results) this.results.innerHTML = '';
        return;
      }

      this.debounceTimer = setTimeout(() => this.fetchResults(query), this.DEBOUNCE_MS);
    },

    async fetchResults(query) {
      if (!this.results) return;
      this.results.innerHTML = '<p class="search-overlay__loading">Searching...</p>';

      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title`;
        const res = await fetch(url, {
          headers: { 'Content-Type': 'application/json' }
        });
        const data = await res.json();
        this.renderResults(data.resources?.results?.products || [], query);
      } catch (e) {
        this.results.innerHTML = '<p class="search-overlay__error">Search unavailable.</p>';
      }
    },

    renderResults(products, query) {
      if (!this.results) return;

      if (!products.length) {
        this.results.innerHTML = `<p class="search-overlay__empty">No results for <strong>"${query}"</strong></p>`;
        return;
      }

      const html = products.map(p => `
        <a class="search-result-item" href="${p.url}">
          ${p.featured_image?.url
            ? `<img src="${p.featured_image.url}&width=80" alt="${p.title}" width="80" height="80" loading="lazy">`
            : ''
          }
          <div class="search-result-item__info">
            <p class="search-result-item__title">${p.title}</p>
            <p class="search-result-item__price">${p.price ? (p.price / 100).toLocaleString('en-IN', { style: 'currency', currency: 'INR' }) : ''}</p>
          </div>
        </a>
      `).join('');

      const viewAll = `<a class="search-overlay__view-all" href="/search?q=${encodeURIComponent(query)}">View all results for "${query}"</a>`;
      this.results.innerHTML = html + viewAll;
    }
  };

  /* =============================================
     INIT ALL MODULES
  ============================================= */
  function init() {
    ThemeToggle.init();
    BackToTop.init();
    StickyHeader.init();
    MobileNav.init();
    AnnouncementBar.init();
    NavDropdowns.init();
    CartQuantity.init();
    Accordion.init();
    Images.init();
    SearchOverlay.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
